'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { DiscoveryPageLayout, SitePageLayout } from '@/platform/layout';
import { DiscussionSortBar } from '@/platform/forum/DiscussionSortBar';
import { ForumPostFeedCard } from '@/platform/forum/ForumPostFeedCard';
import { useAuth } from '@/platform/auth/useAuth';
import { createForumPost, listForumPosts, type ForumPost, type ForumSort } from '@/platform/forum/api';
import { forumComposeReady, type ForumComposeKind } from '@/platform/forum/composeReady';
import { FeedComposeAccordion } from '@/platform/forum/FeedComposeAccordion';
import { useDiscussionComposeMedia } from '@/platform/forum/ForumComposeMediaFields';
import { policyErrorMessage } from '@/platform/youtube/policyMessages';
import { useComposePolicy } from '@/platform/youtube/useComposePolicy';
import { recentPostsEmptyCopy } from '@communio/forum-query';
import { InlineStatus } from '@/platform/feedback/InlineStatus';

type Props = {
  title: string;
  description: string;
  subSlug: string;
  /** Product tour: fixture posts, local compose, no tenant chrome. */
  tourMode?: boolean;
  initialPosts?: ForumPost[];
  /** Tour: open discussion at this path prefix (e.g. /tour/feed/). */
  postHrefPrefix?: string;
  /** Tour: select post without changing the browser URL. */
  onSelectPost?: (postId: string) => void;
  /** Tour: map authorId → display fixture (avoids /users/* Link prefetch). */
  authorFixtures?: Record<string, { displayName?: string; handle?: string; avatarUrl?: string }>;
  onSelectAuthor?: (userId: string) => void;
  beforeFeed?: ReactNode;
};

export function ForumBoardClient({
  title,
  description,
  subSlug,
  tourMode = false,
  initialPosts,
  postHrefPrefix,
  onSelectPost,
  authorFixtures,
  onSelectAuthor,
  beforeFeed,
}: Props) {
  const [sort, setSort] = useState<ForumSort>('hot');
  const [posts, setPosts] = useState<ForumPost[]>(initialPosts ?? []);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(!tourMode && !(initialPosts && initialPosts.length));
  const { isAuthenticated, user } = useAuth();
  const [postTitle, setPostTitle] = useState('');
  const [body, setBody] = useState('');
  const media = useDiscussionComposeMedia({ tourMode });
  const [composeError, setComposeError] = useState('');
  const [composeCollapseKey, setComposeCollapseKey] = useState(0);
  const [composeKind, setComposeKind] = useState<ForumComposeKind | null>(null);
  const { validateTexts } = useComposePolicy();
  const showCompose = tourMode || isAuthenticated;
  const Page = tourMode ? SitePageLayout : DiscoveryPageLayout;
  const activeKind: ForumComposeKind = composeKind ?? 'post';
  const canPost =
    forumComposeReady(
      postTitle,
      body,
      { imageCount: media.imagePaths.length, youtubeUrl: media.youtubeUrl },
      activeKind,
    ) && !media.uploading;

  function resetCompose() {
    setPostTitle('');
    setBody('');
    media.resetMedia();
    setComposeError('');
    setComposeKind(null);
    setComposeCollapseKey((k) => k + 1);
  }

  function prepareKind(kind: ForumComposeKind) {
    if (kind === 'video') media.clearImages();
    else if (kind === 'photo') media.clearYoutube();
    else media.resetMedia();
  }

  useEffect(() => {
    if (tourMode) return;
    const term = new URLSearchParams(window.location.search).get('q') ?? '';
    setQuery(term);
  }, [tourMode]);

  useEffect(() => {
    if (tourMode) {
      setPosts(initialPosts ?? []);
      return;
    }
    setLoading(true);
    listForumPosts({ sort, subSlug, q: query })
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [subSlug, sort, tourMode, initialPosts, query]);

  async function compose() {
    if (!canPost || !composeKind) return;
    const policyErr = validateTexts([postTitle, body, media.youtubeUrl]);
    if (policyErr) {
      setComposeError(policyErr);
      return;
    }
    setComposeError('');
    const imageObjectPaths = composeKind === 'photo' ? media.imagePaths : [];
    const youtubeUrl = composeKind === 'video' ? media.youtubeUrl.trim() : '';
    if (tourMode) {
      const now = new Date().toISOString();
      setPosts((prev) => [
        {
          id: `tour-local-${Date.now()}`,
          authorId: 'tour-you',
          title: postTitle.trim(),
          body: body.trim(),
          subSlug,
          hotScore: 0,
          agreeCount: 0,
          disagreeCount: 0,
          replyCount: 0,
          createdAt: now,
          imageObjectPaths,
          imageUrls: composeKind === 'photo' ? media.imagePreviews : [],
          youtubeVideoId: youtubeUrl.includes('youtu') ? youtubeUrl.slice(-11) : undefined,
        },
        ...prev,
      ]);
      resetCompose();
      return;
    }
    try {
      await createForumPost(postTitle, body, subSlug, {
        imageObjectPaths,
        youtubeUrl,
      });
      resetCompose();
      const refreshed = await listForumPosts({ sort, subSlug, q: query });
      setPosts(refreshed);
    } catch (err) {
      setComposeError(policyErrorMessage(err) ?? (err instanceof Error ? err.message : 'Post failed'));
    }
  }

  return (
    <Page title={title} description={description} toolbar={<DiscussionSortBar value={sort} onChange={setSort} />}>
      {beforeFeed}
      {showCompose && (
        <FeedComposeAccordion
          avatarLetter={(user?.displayName?.[0] ?? user?.email?.[0] ?? (tourMode ? 'Y' : '?')).toUpperCase()}
          title={postTitle}
          body={body}
          onTitleChange={setPostTitle}
          onBodyChange={setBody}
          youtubeUrl={media.youtubeUrl}
          onYoutubeUrl={media.setYoutubeUrl}
          imagePreviews={media.imagePreviews}
          uploading={media.uploading}
          imageCount={media.imagePaths.length}
          onAddFiles={(files) => void media.addPhotos(files)}
          canPost={canPost}
          onPost={compose}
          error={composeError || media.uploadError}
          showLegal={!tourMode}
          collapseKey={composeCollapseKey}
          onKindChange={setComposeKind}
          onPrepareKind={prepareKind}
        />
      )}

      <div className="social-feed" role="feed">
        {loading ? (
          <InlineStatus loading />
        ) : posts.length === 0 ? (
          <InlineStatus
            empty={recentPostsEmptyCopy(query, 'No posts yet. Be the first to start the conversation.')}
          />
        ) : (
          posts.map((p) => (
            <ForumPostFeedCard
              key={p.id}
              post={p}
              disableNavigation={tourMode && !postHrefPrefix && !onSelectPost}
              hrefOverride={postHrefPrefix ? `${postHrefPrefix}${p.id}/` : undefined}
              onSelect={onSelectPost}
              authorFixture={authorFixtures?.[p.authorId]}
              onSelectAuthor={onSelectAuthor}
              tourMode={tourMode}
              onPostChange={(next) => {
                setPosts((prev) => prev.map((row) => (row.id === next.id ? { ...row, ...next } : row)));
              }}
            />
          ))
        )}
      </div>
    </Page>
  );
}
